import React, { useEffect, useRef, useState } from "react";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents } from "lib/utils/constants";
import "altcha";
import { CaptchaState, CaptchaStatus } from "./CaptchaWrapper";
import { useMerchantContext } from "lib/contexts/MerchantProvider";

const ALTCHA_DOMAIN = import.meta.env.VITE_ALTCHA_DOMAIN;
const API_KEY = import.meta.env.VITE_ALTCHA_API_KEY;

interface AltchaProps {
  context: string;
  onTokenChange: (token: CaptchaState) => void;
}

const Altcha: React.FC<AltchaProps> = ({ onTokenChange, context }) => {
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const [loadStartTime, setLoadStartTime] = useState<number | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const {
    state: { merchant },
  } = useMerchantContext();

  const enableTest = window.location.hostname === "localhost" || merchant?.automationEnabled

  useEffect(() => {
    setLoadStartTime(Date.now());
  }, []);

  useEffect(() => {
    const handleStateChange = (event: Event | CustomEvent) => {
      if ("detail" in event) {
        const { state, payload } = event?.detail;
        if (state === "verified" && payload) {
          onTokenChange({ token: payload, status: CaptchaStatus.SUCCESS });

          if (loadStartTime) {
            const timeTaken = Date.now() - loadStartTime;
            if (timeTaken > 5000) {
              sendAnalyticsEvent({
                eventName: analyticsEvents.FLO_TURNSTILE_LOAD_TIME_EXCEEDED,
                eventType: "flo_action",
                metaData: {
                  turnstileData: {
                    context,
                    captchaProvider: "ALTCHA",
                    timeTaken: timeTaken,
                  },
                },
              });
            }
          }
        } else if (state === "error") {
          onTokenChange?.({ token: null, status: CaptchaStatus.ERROR });
          sendAnalyticsEvent({
            eventName: analyticsEvents.FLO_TURNSTILE_ERROR,
            eventType: "flo_action",
            metaData: {
              context,
              captchaProvider: "ALTCHA",
              errorCode: payload || "ALTCHA_ERROR",
            },
          });
        }
      }
    };
    const { current } = widgetRef;

    if (current) {
      current.addEventListener("statechange", handleStateChange);
    }

    return () => {
      if (current) {
        current.removeEventListener("statechange", handleStateChange);
      }
    };
  }, [onTokenChange, loadStartTime, context, sendAnalyticsEvent]);

  return (
    <div className={`min-w-full`}>
      <altcha-widget
        ref={widgetRef}
        style={{
          "--altcha-border-width": "1px",
          "--altcha-border-radius": "12px",
          "--altcha-color-base": "var(--flo-primary-dark-color)",
          "--altcha-color-border": "var(--flo-primary-dark-color)",
          "--altcha-color-text": "var(--flo-primary-btn-text-color)",
          "--altcha-color-border-focus": "var(--flo-primary-dark-color)",
          "--altcha-color-error-text": "var(--flo-primary-btn-text-color) ",
          "--altcha-color-footer-bg": "var(--flo-primary-dark-color)",
          "--altcha-max-width": "100%",
        }}
        hidelogo
        hidefooter
        auto="onload"
        {...(enableTest && { test: true })}
        challengeurl={`${ALTCHA_DOMAIN}/v1/challenge?apiKey=${API_KEY}`}></altcha-widget>
    </div>
  );
};

export default Altcha;
